import React, { useEffect, useState } from 'react';
import Menu from '../menu/Menu';
import { apiGet } from '../../api/apiClient';
import RTSpinner from '../common/RTSpinner';
import AdminDashboard from './AdminDashboard';
import StaffDashboard from './StaffDashboard';
import PGDashboard from './PGDashboard';
import { toastError } from '../common/toast';
import './Home.css';
import '../common/rtCommon.css';
import tenantStorage from '../../utils/tenantStorage';

const Home = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    // Get user role from localStorage
    const role = tenantStorage.getItem('userRole') || 'RT_NoAccess';
    setUserRole(role);
    
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const response = await apiGet('/dashboard');
        
        // Check if the response contains an error
        if (response.data.error) {
          if (response.status === 401) {
            toastError('Session expired. Please log in again.');
            setError('Session expired. Please log in again.');
          } else {
            toastError(response.data.error_message || 'Failed to load dashboard data');
            setError(response.data.error_message || 'Failed to load dashboard data');
          }
          return;
        }
        
        setDashboardData(response.data.data);
        
        // Store staff_id in localStorage if available (for RT_Staff users)
        if (response.data.staff_id) {
          tenantStorage.setItem('staffId', response.data.staff_id.toString());
        }
        if (response.data.staff_name) {
          tenantStorage.setItem('staffName', response.data.staff_name);
        }
        
      } catch (err) {
        if (err.response && err.response.status === 401) {
          // Unauthorized access - possibly token expired
          toastError('Session expired. Please log in again.');
          setError('Session expired. Please log in again.');
          // Optionally, redirect to login page
        } else {
          const errorMessage = 'Failed to load dashboard data';
          toastError(errorMessage);
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();  
  }, []);

  // Function to render appropriate dashboard based on user role
  const renderDashboard = () => {
    if (loading) {
      return <RTSpinner message="Loading dashboard..." fullScreen={false} />;
    }
    
    if (refreshing) {
      return <RTSpinner message="Refreshing dashboard..." fullScreen={true} />;
    }
    
    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }

    // Handle access denied case
    if (dashboardData && dashboardData.error_message) {
      return <div className="alert alert-danger">{dashboardData.error_message}</div>;
    }

    // Function to refresh dashboard data
    const refreshDashboard = async () => {
      setRefreshing(true);
      setError(''); // Clear any existing errors
      try {
        const response = await apiGet('/dashboard');
        
        // Check if the response contains an error
        if (response.data.error) {
          toastError(response.data.error_message || 'Failed to refresh dashboard data');
          setError(response.data.error_message || 'Failed to refresh dashboard data');
          return;
        }
        
        setDashboardData(response.data.data);
        
        // Store staff_id in localStorage if available (for RT_Staff users)
        if (response.data.staff_id) {
          tenantStorage.setItem('staffId', response.data.staff_id.toString());
        }
        if (response.data.staff_name) {
          tenantStorage.setItem('staffName', response.data.staff_name);
        }
        
      } catch (err) {
        console.error('Error refreshing dashboard:', err);
        toastError('Failed to refresh dashboard data');
        setError('Failed to refresh dashboard data');
      } finally {
        setRefreshing(false);
      }
    };

    switch (userRole) {
      case 'RT_Admin':
        return <AdminDashboard data={dashboardData} onDataRefresh={refreshDashboard} />;
      case 'RT_Manager':
      case 'RT_Staff':
        return <StaffDashboard data={dashboardData} onDataRefresh={refreshDashboard} />;
      case 'PayingGuests':
        return <PGDashboard data={dashboardData} />;
      default:
        return <div className="alert alert-warning">Access denied. Please contact management.</div>;
    }
  };

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" />
      <div className="rt-main-content">
        {renderDashboard()}
      </div>
    </div>
  );
};

export default Home;